// TiptapRichPreview.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ImageContext } from '../../contexts/ImageContext';
import { CameraContext } from '../../contexts/CameraContext';
import useAuth from '../../hooks/useAuth';
import axios from "axios";
import { GalleryContentContext } from '../../contexts/GalleryContentContext';

// --- Context for Stable Accordion Item IDs ---
const AccordionItemContext = createContext(null);

const InlineChromiumBugfix = () => (
  <span
    contentEditable={false}
    style={{
      fontSize: "0",
    }}
  >
    {String.fromCodePoint(160) /* Non-breaking space */}
  </span>
);

const LinkComponent = ({ attrs, children }) => {
  return (
    <a
      className="text-green6 pointer fw-bold"
      href="#"
      onClick={(e) => {
        e.preventDefault();
        if (attrs?.href) {
          window.open(attrs.href, "_self");
        }
      }}
    >
      <InlineChromiumBugfix />
      {children}
      <InlineChromiumBugfix />
    </a>
  );
};

const ModelLinkComponent = ({ attrs, children }) => {
  const { setCamera } = useContext(CameraContext);
  return (
    <button
      className="btn btn-link p-0 text-primary fw-bold"
      onClick={(e) => {
        e.preventDefault();
        if (setCamera && attrs?.target && attrs?.orbit) {
          setCamera(attrs.target, attrs.orbit);
        }
      }}
    >
      {children}
    </button>
  );
};

const ImageLinkComponent = ({ attrs, children }) => {
  const { level1, level2, level3, level4 } = useParams();
  const imageId = attrs?.imageId;
  return (
    <Link
      className="pointer fw-bold"
      style={{ color: "#d56d10" }}
      state={{ imageId }}
      to={`/${level1}/${level2}/${level3}/${level4}/image/${imageId}`}
    >
      <InlineChromiumBugfix />
      {children}
      <InlineChromiumBugfix />
    </Link>
  );
};

const ContentLinkComponent = ({ attrs, children }) => {
  return (
    <a
      className="text-green6 pointer fw-bold"
      href="#"
      onClick={(e) => {
        e.preventDefault();
        const path = attrs?.href || attrs?.url || "";
        const absolute = path.startsWith("/") ? path : `/${path}`;
        window.open(`${absolute}`, "_self");
      }}
    >
      <InlineChromiumBugfix />
      {children}
      <InlineChromiumBugfix />
    </a>
  );
};

const FormattedText = ({ textNode }) => {
  let content = textNode.text;
  if (!textNode.marks || textNode.marks.length === 0) {
    return <>{content}</>;
  }

  // Sequentially wrap text based on active Tiptap marks
  return textNode.marks.reduce((acc, mark) => {
    switch (mark.type) {
      case "bold":
        return <strong>{acc}</strong>;
      case "italic":
        return <em>{acc}</em>;
      case "underline":
        return <u>{acc}</u>;
      case "strike":
        return <s>{acc}</s>;
      case "code":
        return <code>{acc}</code>;
      case "link":
        return <LinkComponent attrs={mark.attrs}>{acc}</LinkComponent>;
      case "contentLink":
        return <ContentLinkComponent attrs={mark.attrs}>{acc}</ContentLinkComponent>;
      case "imageLink":
        return <ImageLinkComponent attrs={mark.attrs}>{acc}</ImageLinkComponent>;
      case "modelLink":
        return <ModelLinkComponent attrs={mark.attrs}>{acc}</ModelLinkComponent>;
      case "label":
        return <span className="custom-label">{acc}</span>;
      case "marker":
        return (
          <span style={{ background: mark.attrs?.color || mark.attrs?.background || "yellow" }}>
            {acc}
          </span>
        );
      case "h1":
        return <span className="h1">{acc}</span>;
      case "h2":
        return <span className="h2">{acc}</span>;
      case "h3":
        return <span className="h3">{acc}</span>;
      case "h4":
        return <span className="h4">{acc}</span>;
      case "h5":
        return <span className="h5">{acc}</span>;
      case "h6":
        return <span className="h6">{acc}</span>;
      default:
        return acc;
    }
  }, content);
};

const AccordionItemComponent = ({ children }) => {
  // Stable random ID initialized once on mount
  const [itemId] = useState(() => `acc-${Math.random().toString(36).substring(2, 9)}`);
  return (
    <AccordionItemContext.Provider value={itemId}>
      <div className="accordion-item mb-1">
        {children}
      </div>
    </AccordionItemContext.Provider>
  );
};

const AccordionTitleComponent = ({ children }) => {
  const itemId = useContext(AccordionItemContext);
  return (
    <h2 className="accordion-header" id={`${itemId}-header`}>
      <button
        className="accordion-button rounded-3 text-gray1 fw-bold collapsed"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target={`#${itemId}-collapse`}
        aria-expanded="false"
        aria-controls={`${itemId}-collapse`}
      >
        {children}
      </button>
    </h2>
  );
};

const AccordionContentComponent = ({ children }) => {
  const itemId = useContext(AccordionItemContext);
  return (
    <div
      id={`${itemId}-collapse`}
      className="accordion-collapse collapse"
      aria-labelledby={`${itemId}-header`}
      data-bs-parent="#mainAccordion"
    >
      <div className="accordion-body">
        {children}
      </div>
    </div>
  );
};

const TiptapRichPreview = ({ node }) => {
  const { level1, level2, level3, level4 } = useParams();
  const { imageDispatch } = useContext(ImageContext);
  const { galleryContentShow } = useContext(GalleryContentContext);
  const { user } = useAuth();

  const [url, setUrl] = useState("");

  const type = node?.type;
  const attrs = node?.attrs;
  const content = node?.content;

  useEffect(() => {
    if (type === "image" && attrs?.imageId) {
      axios
        .post(
          process.env.REACT_APP_API_URL + "/get-content-images",
          { imageIds: [attrs.imageId] },
          { headers: { Authorization: "Bearer " + user?.jwtToken } }
        )
        .then((res) => {
          if (res.data?.length) {
            const img = res.data[0];
            setUrl(process.env.REACT_APP_MEDIA_URL + "/" + img.url);
          }
        });

      imageDispatch({ type: "add", imageId: attrs.imageId });
    }

    if ((type === "imageBlock" || type === "image-block") && attrs?.images) {
      attrs.images.forEach(img => {
        if (img?.id) {
          imageDispatch({ type: "add", imageId: img.id });
        }
      });
    }
  }, [type, attrs, user, imageDispatch]);

  if (!node) return null;

  const renderChildren = () => {
    if (!content) return null;
    return content.map((child, i) => (
      <TiptapRichPreview key={i} node={child} />
    ));
  };

  switch (type) {
    case "doc":
      return <>{renderChildren()}</>;

    case "paragraph":
      if (!content || content.length === 0) {
        return <p className="m-0">&nbsp;</p>;
      }
      return <p className="m-0">{renderChildren()}</p>;

    case "text":
      return <FormattedText textNode={node} />;

    case "heading": {
      const Tag = `h${attrs?.level || 1}`;
      return <Tag className="fw-bold">{renderChildren()}</Tag>;
    }

    case "bulletList":
    case "bullet-list":
      return <ul className="m-0">{renderChildren()}</ul>;

    case "orderedList":
    case "ordered-list": {
      const listType = attrs?.type || "1";
      const start = attrs?.start || 1;
      let className = "m-0";
      if (listType === "A") className = "alpha m-0";
      else if (listType === "I") className = "roman ps-3";
      
      return (
        <ol start={start} type={listType} className={className}>
          {renderChildren()}
        </ol>
      );
    }

    case "listItem":
    case "list-item":
      return <li>{renderChildren()}</li>;

    case "accordion":
    case "accordion-wrapper":
      return (
        <div className="accordion accordion-flush" id="mainAccordion">
          {renderChildren()}
        </div>
      );

    case "accordionItem":
    case "accordion-item":
      return <AccordionItemComponent>{renderChildren()}</AccordionItemComponent>;

    case "accordionTitle":
    case "accordion-title":
      return <AccordionTitleComponent>{renderChildren()}</AccordionTitleComponent>;

    case "accordionContent":
    case "accordion-content":
      return <AccordionContentComponent>{renderChildren()}</AccordionContentComponent>;

    case "table":
      return (
        <table className="preview-table">
          <tbody>{renderChildren()}</tbody>
        </table>
      );

    case "tableRow":
    case "table-row":
      return <tr>{renderChildren()}</tr>;

    case "tableCell":
    case "table-cell":
      return <td>{renderChildren()}</td>;

    case "tableHeader":
    case "table-header":
      return <th className="fw-bold">{renderChildren()}</th>;

    case "imageBlock":
    case "image-block":
      if (!galleryContentShow) return null;
      return (
        <div className="row row-cols-auto">
          {(attrs?.images || []).map((image, idx) => {
            if (!image || !image.id) return null;
            return (
              <div className="col mb-3" key={image.id || idx}>
                <div
                  className="d-flex border border-gray3 overflow-hidden border-5 bg-white rounded position-relative"
                  style={{ height: "10rem" }}
                  id={image.id}
                >
                  <img
                    src={`${process.env.REACT_APP_MEDIA_URL}/${image.url}`}
                    className="h-100"
                    alt=""
                  />
                  <Link
                    className="stretched-link pointer"
                    state={{ imageId: image.id }}
                    to={`/${level1}/${level2}/${level3}/${level4}/image/${image.id}`}
                  >
                    <span className="text-green1 position-absolute bottom-0 end-0 me-1">
                      <span className="material-icons">fullscreen</span>
                    </span>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      );

    case "imageList":
    case "image-list":
      return galleryContentShow ? (
        <div className="row row-cols-auto">{renderChildren()}</div>
      ) : null;

    case "image":
      if (!url) return null;
      return (
        <div className="col mb-3">
          <div
            className="d-flex border border-gray3 overflow-hidden border-5 bg-white rounded position-relative"
            style={{ height: "10rem" }}
            id={attrs?.imageId}
          >
            <img src={url} className="h-100" alt="" />
            <Link
              className="stretched-link pointer"
              state={{ imageId: attrs?.imageId }}
              to={`/${level1}/${level2}/${level3}/${level4}/image/${attrs?.imageId}`}
            >
              <span className="text-green1 position-absolute bottom-0 end-0 me-1">
                <span className="material-icons">fullscreen</span>
              </span>
            </Link>
          </div>
        </div>
      );

    case "videoList":
    case "video-list":
      return (
        <figure>
          <div className="image-group">{renderChildren()}</div>
        </figure>
      );

    case "video":
      return attrs?.url ? (
        <figure>
          <div className="img-thumb">
            <iframe
              width="100%"
              height="auto"
              src={attrs.url}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <Link to="#">
              <span className="img-thumb-btn text-decoration-none">
                <i className="icon-general icon-zoom"></i>
              </span>
            </Link>
          </div>
          <figcaption>{attrs.title}</figcaption>
        </figure>
      ) : null;

    case "hardBreak":
    case "hard-break":
      return <br />;

    case "horizontalRule":
    case "horizontal-rule":
      return <hr />;

    default:
      return <>{renderChildren()}</>;
  }
};

export default TiptapRichPreview;